import findUp from 'find-up';
import { existsSync } from 'fs';
import { dirname, resolve } from 'path';
import { BuildFlags } from '../../commands/build/utilities/types';
import { ProjectConfig } from '../../types';
import logger from '../logger';
import loadConfig from './loadConfig';
import resolveConfigPaths from './resolveConfigPaths';
import validateConfig from './validateConfig';
import computeFullProjectConfig from './computeFullProjectConfig';

export default function getProjectConfig(
  configPath?: string,
  flags?: { build?: Pick<BuildFlags, 'ignoreExtendsErrors'> },
): {
  config: ProjectConfig;
  unprocessedConfig: ProjectConfig;
  projectRoot: string;
} {
  // If no config path is provided, we'll try to find one
  if (!configPath) {
    logger.debug('No config path provided, searching for one');
    configPath = findUp.sync([
      'todesktop.json',
      'todesktop.js',
      'todesktop.ts',
    ]);
    if (!configPath) {
      throw new Error(
        'Can not find todesktop.json, todesktop.js, or todesktop.ts in this folder or any parent folders',
      );
    }
  } else {
    configPath = resolve(process.cwd(), configPath);

    // If the config path is provided
    if (!existsSync(configPath)) {
      logger.error('Provided config path does not exist');
      throw new Error(`Config file not found at ${configPath}`);
    }
  }

  const projectRoot = dirname(configPath);

  // This will throw if it's not valid JSON
  const partialConfig = loadConfig(configPath);

  // compute the full project config
  const config = computeFullProjectConfig(partialConfig, projectRoot, flags);
  const configForValidation = {
    ...config,
    appPath: config.appPath || '.',
  };

  // This will throw if invalid
  validateConfig({ config: configForValidation, projectRoot });

  const result = resolveConfigPaths({
    config: configForValidation,
    projectRoot,
  });

  // Used by `tdutils cli`
  if (process.env.TODESKTOP_CLI_APP_ID) {
    result.id = process.env.TODESKTOP_CLI_APP_ID;
  }

  return { config: result, unprocessedConfig: config, projectRoot };
}
